import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useTeamContext } from '@/context/TeamContext'
import {
	getPlanDisplayName,
	normalizePlanId,
	type CanonicalPlanId,
} from '@/lib/entitlements'

export interface PlanInfo {
	planId: CanonicalPlanId
	planName: string
	isPremium: boolean
	subscription: {
		status: string | null
		customerId: string | null
		subscriptionId: string | null
	}
}

export function usePlanInfo() {
	const { teamId } = useTeamContext()


	const queryResult = useQuery<PlanInfo, Error>({
		queryKey: ['planInfo', teamId],
		queryFn: async (): Promise<PlanInfo> => {

			if (!teamId) {
				return {
					planId: 'free',
					planName: getPlanDisplayName('free'),
					isPremium: false,
					subscription: {
						status: null,
						customerId: null,
						subscriptionId: null,
					},
				}
			}

			try {
				const { data: team, error } = await supabase
					.from('teams')
					.select(
						'plan_id, subscription_status, stripe_customer_id, stripe_subscription_id'
					)
					.eq('id', teamId)
					.single()


				if (error) {
					throw error
				}

				if (!team) {
					throw new Error('Team not found')
				}

				// Determine if the subscription is premium based on status
				const isPremium =
					team.subscription_status === 'active' ||
					team.subscription_status === 'trialing'


				// Use the plan_id if available, otherwise determine based on subscription
				const planId = normalizePlanId(
					team.plan_id || (isPremium ? 'starter' : 'free')
				)
				const planName = getPlanDisplayName(planId)

				const result = {
					planId,
					planName,
					isPremium,
					subscription: {
						status: team.subscription_status,
						customerId: team.stripe_customer_id,
						subscriptionId: team.stripe_subscription_id,
					},
				}

				return result
			} catch (err) {
				// Default to free plan on error for safety
				const fallbackResult = {
					planId: 'free' as const,
					planName: getPlanDisplayName('free'),
					isPremium: false,
					subscription: {
						status: null,
						customerId: null,
						subscriptionId: null,
					},
				}
				return fallbackResult
			}
		},
		enabled: !!teamId,
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes
		gcTime: 15 * 60 * 1000,
		refetchOnWindowFocus: true, // Refetch plan info on window focus
	})






	return queryResult
}
